import { useScrollPostionRestoration } from '@/utils/dom'
import usePostDrawer from '@/utils/hooks/usePostDrawer'
import { Box, Container, ScrollArea } from '@mantine/core'
import { motion } from 'motion/react'
import type { HTMLAttributes } from 'react'
import { type ReactNode, type Ref, useRef } from 'react'
import PostDrawer from '../PostDrawer/PostDrawer'
import Sidebar from '../Sidebar'
import { SIDEBAR_WIDTH } from '../Sidebar/config'
import { useStoryViewer } from '../Story/viewer/StoryViewerContext'
import styles from './index.module.css'
import { ScrollAreaProvider } from './ScrollAreaContext'

export interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
	children?: ReactNode
	ref?: Ref<HTMLDivElement>
}

export default function Layout({ children, ref, ...props }: LayoutProps) {
	const { data: post, isOpen, close } = usePostDrawer()
	const { isStoryViewerOpen } = useStoryViewer()
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const { cacheScrollPosition } = useScrollPostionRestoration(scrollContainerRef)

	return (
		<ScrollArea
			ref={ref}
			h="100vh"
			viewportRef={scrollContainerRef}
			{...props}
			inert={isStoryViewerOpen ? true : undefined}
			aria-hidden={isStoryViewerOpen ? true : undefined}
			onScrollPositionChange={(position) => {
				cacheScrollPosition(position.y)
			}}
		>
			<ScrollAreaProvider value={{ scrollContainerRef }}>
				<Sidebar />
				<Box
					className={styles.mainContainer}
					style={{
						'--sidebar-width': `${SIDEBAR_WIDTH}px`,
					}}
				>
					<Container>
						<motion.div
							animate={{
								x: isOpen ? 'var(--post-drawer-main-offset-x)' : '0%',
							}}
							transition={{
								type: 'spring',
								damping: 20,
								stiffness: 100,
							}}
							style={{
								position: 'relative',
								zIndex: 1,
							}}
						>
							{children}
						</motion.div>
					</Container>

					<PostDrawer data={post} opened={isOpen} onClose={close} />
				</Box>
			</ScrollAreaProvider>
		</ScrollArea>
	)
}
